<template>
  <div class="h-100 px-3">
    <!-- Dashboard Cards -->
    <div v-if="$route.name === 'personalDashboard' && Object.keys(summary).length" class="row mb-4 mt-3 dashboard-cards-container">
       <div v-for="(group, key) in summary" :key="key" class="col-md-2 mb-1">
          <div class="kpi-card shadow-sm rounded p-3 bg-white h-100 position-relative overflow-hidden cursor-pointer" @click="filterByGroup(key)">
             <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="group-name mb-0 font-weight-bold text-uppercase" style="letter-spacing: 0.5px;">{{ group.group_name }}</h6>
                <div class="total-badge">{{ group.total }}</div>
             </div>
             <div class="status-list mt-1">
                <div v-for="status in group.statuses" :key="status.id" 
                     class="status-item d-flex justify-content-between align-items-center py-1 border-bottom-faint cursor-pointer px-2"
                     :class="{'selected-status-highlight': selectedStatus === status.id}"
                     @click.stop="filterByStatus(key, status.id)">
                   <div class="d-flex align-items-center flex-row-reverse">
                      <span class="status-name text-muted small">{{ status.name }}</span>
                      <div class="status-dot ml-2" :style="{backgroundColor: status.color, border: '1px solid rgba(0,0,0,0.1)'}"></div>
                   </div>
                   <span class="status-count font-weight-bold small">{{ status.count }}</span>
                </div>
             </div>
             <div class="card-footer-decorator" :class="'color-' + key"></div>
          </div>
       </div>
    </div>

    <template v-if="dataTableReady">
      <datatable
        :key="tableKey"
        :storageName="tableStorageName"
        :tcols="cols"
        :trows="rows"
        :tconfig="config"
        :pages_chunk="pages_chunk"
        :rows_count="rows_count"/>
    </template>
    <template v-else>
      <loader/>
    </template>
  </div>
</template>
<script>
import datatable from '@/components/datatable'
import axios from '../axios'
import _ from 'lodash'
import loader from '@/components/loader'

export default {
  components: {datatable, loader},

  // created () {},
  name: 'TableController',
  mounted() {
   // this.appendData()

  },

  watch: {
    '$route.name': {
      handler: function (name) {
        this.dataTableReady = false
        this.appendData()
      },
      deep: true,
      immediate: true
    },
    '$store.state.crm.modeSource': {
      handler: function (newModeSource) {
        // Refresh data when modeSource changes
        this.dataTableReady = false
        this.appendData()
      },
      immediate: false
    }
  },

  data: function () {
    return {
      dataTableReady: false,
      cols: [],
      rows: [],
      rows_count: 0,
      config: {},
      options: {},
      is_loading: false,
      pages_chunk: 25,
      page_p: 1,
      query_params: false,
      summary: {},
      selectedGroup: null,
      selectedStatus: null,
      tableKey: 'orders',
      tableStorageName: 'personalDashboard_orders',
    }
  },

  methods: {
    getSummary() {
      const modeSource = this.$store.getters.currentModeSource || 'hova';
      return axios.post('/orders/getDashboardSummary', { modeSource }).then(res => {
        if (res.data.success) {
          this.summary = res.data.data;
        }
      }).catch(err => {
        console.error('Error fetching dashboard summary:', err);
      });
    },
    filterByGroup(groupKey) {
      if (this.selectedGroup === groupKey) {
        this.selectedGroup = null; 
        this.selectedStatus = null;
      } else {
        this.selectedGroup = groupKey;
        this.selectedStatus = null;
      }
      this.dataTableReady = false;
      this.appendData();
    },
    filterByStatus(groupKey, statusId) {
      this.selectedGroup = groupKey;
      if (this.selectedStatus === statusId) {
        this.selectedStatus = null;
      } else {
        this.selectedStatus = statusId;
      }
      this.dataTableReady = false;
      this.appendData();
    },
    async appendData() {
      if (this.$route.name === 'personalDashboard') {
        await this.getSummary();
      }

      this.is_loading = true;
      
      try {
        // 1. Load Configuration
        const configModule = await import('../table-config');
        const allConfigs = configModule.default;
        
        let configKey = this.$route.name === 'personalDashboard' ? 'orders' : this.$route.name;
        if (this.$route.name === 'personalDashboard') {
          if (this.selectedGroup && this.summary[this.selectedGroup]) {
            if (this.summary[this.selectedGroup].model_type === 'leads') {
              configKey = 'leads';
            }
          } else {
            const groups = Object.values(this.summary);
            const hasLeads = groups.some(g => g.model_type === 'leads');
            const hasOrders = groups.some(g => g.model_type === 'orders');
            if (hasLeads && !hasOrders) {
              configKey = 'leads';
            }
          }
        }
        this.config = _.cloneDeep(allConfigs[configKey] || {});
        // Hide filters in dashboard view
        this.config.cfilters = null;
        this.config.initFilters = null;
        this.config.selectable = true;
        
        this.tableKey = configKey + '_' + (this.selectedGroup || 'all') + '_' + (this.selectedStatus || 'all');
        this.tableStorageName = 'personalDashboard_' + configKey;
        let ordersPages = ['personalDashboard', 'orders', 'ordersControl', 'ordersProtect', 'ordersCancellations', 'ordersCollection', 'ordersTasks', 'ordersClaims'];
        let url = ordersPages.includes(this.$route.name) ? 'insuranceCompanyFilter' : 'all';
        const routeName = ordersPages.includes(this.$route.name) ? 'orders' : this.$route.name;
        let url_page = '';
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('page')) {
          url_page = "/" + urlParams.get('page');
        }
        let data = Object.assign({}, this.$route.query);
        data.routeName = this.$route.name;
        data.selectedGroup = this.selectedGroup;
        data.selectedStatus = this.selectedStatus;
        data.pages_chunk = data.pages_chunk || 25;
        data.modeSource = this.$store.getters.currentModeSource || 'hova';
        const res = await axios.post(`/${routeName}/${url}${url_page}`, data);
        const resData = res.data.data.rows;
        if (this.config.tableName === 'leads' && this.config.schema) {
          this.cols = this.config.schema.map(r => r.name);
          if (!this.cols.includes('actions')) {
            this.cols.unshift('actions');
          }
          // Force datatable to show all columns by updating initDisplayCols
          this.config.initDisplayCols = this.cols;
        } else {
          // Standard Order Logic
          let resKeys = _.isObject(resData[0]) ? Object.keys(resData[0]) : resData;
          if (this.config.initDisplayCols) {
            this.cols = this.config.initDisplayCols;
          } else {
            this.cols = _.without(resKeys, 'userId', 'updated_at');
            if (!this.config.readonly && !this.cols.includes('actions')) {
              this.cols.unshift('actions');
            }
          }
        }

        this.rows = _.isObject(resData[0]) ? resData : [];
        
        // 5. Handle Options and Metadata
        if (res.data.data.options) {
          this.rows_count = res.data.data.options.total_rows || 0;
          if (!this.config.options) this.$set(this.config, 'options', {});
          
          for (let i in res.data.data.options) {
            this.$set(this.config.options, i, res.data.data.options[i]);
          }
        }

        this.dataTableReady = true;
      } catch (err) {
        console.error('Error in dashboard appendData:', err);
        this.$emit('alert', 'danger', this.$t('message.tableNotFound'));
      } finally {
        this.is_loading = false;
      }
    }
  }
}
</script>
<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.kpi-card {
  transition: all 0.3s ease;
  border: 1px solid rgba(0,0,0,0.05);
  min-height: 140px;
}
.kpi-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
}
.total-badge {
  background: #f8f9fa;
  color: #333;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 800;
  font-size: 0.9rem;
  border: 1px solid #eee;
}
.border-bottom-faint {
  border-bottom: 1px solid rgba(0,0,0,0.03);
}
.status-item:last-child {
  border-bottom: none;
}
.card-footer-decorator {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: #eee;
}

/* Group Colors */
.color-orders { background: #4e73df !important; }
.color-ordersCancellations { background: #e74a3b !important; }
.color-ordersCollection { background: #f6c23e !important; }
.color-ordersTasks { background: #1cc88a !important; }
.color-ordersClaims { background: #36b9cc !important; }
.color-ordersControl { background: #858796 !important; }
.color-ordersProtect { background: #5a5c69 !important; }

.group-name {
  color: #4a4a4a;
}
.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  box-shadow: 0 0 5px rgba(0,0,0,0.1);
}
.selected-status-highlight {
  background: #f0f4ff;
  border-left: 4px solid #4e73df;
  border-radius: 4px;
}
</style>
